// @flow
import React, { useContext, useState, forwardRef, useEffect } from 'react';

import { useRouter } from 'Client/router/useRouter.js';
import { ReactPageContext } from 'Context/pageContext';
import ReactResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { getFromLocalStorage, setInLocalStorage } from 'Client/local-storage-api';
import { PromoBanner, type PromoBannerProps } from './promo-banner/PromoBanner.jsx';

const PROMOTIONALBANNER_LOCALSTORAGE_KEY = 'disablePromotionalBanner';

type Props = {
  ...$Exact<PromoBannerProps>,
  // a page is OR the code of a village ("AGAC") or a url ("/")
  enabledPages?: Array<?string>,
  disabledPages?: Array<?string>,
};

function checkDuration() {
  const storaged = getFromLocalStorage(PROMOTIONALBANNER_LOCALSTORAGE_KEY);
  if (!storaged) return true;

  const now = Date.now();

  const storagedDate = new Date(Number(storaged));
  const tomorrow = storagedDate.setDate(storagedDate.getDate() + 1);

  return now - tomorrow > 0;
}
const PromotionalBannerClass = ({
  enabledPages = [],
  disabledPages = [],
  ...bannerProps
}: Props) => {
  const { location } = useRouter<*, *>();
  const pageContext = useContext(ReactPageContext);
  const responsiveContext = useContext(ReactResponsiveContext);

  const pageMatch = pages => {
    const { pageId } = pageContext;

    return pages.some(
      page =>
        page &&
        // testing pages that correspond to a resort/tour
        (pageId === `resort-${page.toUpperCase()}` ||
          pageId === `tour-${page.toUpperCase()}` ||
          // pages that correspond to a url
          location.pathname === page ||
          location.pathname.startsWith(`${page}/`)),
    );
  };

  const isPageEnabled = enabledPages.length ? pageMatch(enabledPages) : true;
  const isPageDisabled = disabledPages.length ? pageMatch(disabledPages) : false;

  const shouldBeDisplayed = () =>
    responsiveContext.isAppMounted && checkDuration() && isPageEnabled && !isPageDisabled;

  const [isDisplayed, setDisplay] = useState(shouldBeDisplayed());

  useEffect(
    () => {
      setDisplay(shouldBeDisplayed());
    },
    [enabledPages, responsiveContext],
  );

  const disable = () => {
    setDisplay(false);
    setInLocalStorage(PROMOTIONALBANNER_LOCALSTORAGE_KEY, Date.now());
  };

  if (!isDisplayed) {
    return null;
  }

  const trackingData = {
    event: 'eventGA',
    categoryGA: 'promoBanner',
    labelGA: bannerProps.title,
    actionGA: 'click',
    valueGA: '0',
    nonInteraction: '0',
  };

  const trackingDataClose = {
    event: 'eventGA',
    categoryGA: 'promoBanner',
    labelGA: bannerProps.title,
    actionGA: 'close',
    valueGA: '0',
    nonInteraction: '0',
  };

  return (
    <PromoBanner
      {...bannerProps}
      onClose={disable}
      trackingData={trackingData}
      trackingDataClose={trackingDataClose}
    />
  );
};

// $FlowFixMe
export const PromotionalBanner = forwardRef((props, ref) => (
  <PromotionalBannerClass innerRef={ref} {...props} />
));
